import { marked } from 'marked';
import React from 'react';
import { MutableRefObject } from 'react';
import { Params } from 'react-chatbotify'; 
import renderMathInElement from 'katex/contrib/auto-render';

export enum AIApplication {
  SMART_TICKETING = 0,
  USER_GUIDE = 1,
  ASSISTANT = 2,
  INTEGRATION_ASSISTANT = 3
}

export type AIAssistantTabs = 'Trouble Ticket Assistant' | 'User Guide' | 'Telco Assistant'| 'Integration Assistant';

export const closeAIApiCallEventName = 'Close_AI_API_Call';

export const closeAIApiCallEvent = new Event(closeAIApiCallEventName);

export const replaceUnicodeEscapes = (str: string): string => {
  // Use a regular expression to find all Unicode escape sequences in the string
  return str.replace(/\\u[\dA-Fa-f]{4}/g, (match) => {
    // Convert each escape sequence to its corresponding character
    return String.fromCharCode(parseInt(match.replace('\\u', ''), 16));
  });
};

export const generateUUID = (): string => {
  const hexDigits = '0123456789abcdef';
  let uuid = '';
  for (let i = 0; i < 36; i++) {
    if (i === 8 || i === 13 || i === 18 || i === 23) {
      uuid += '-';
    } else if (i === 14) {
      uuid += '4';
    } else if (i === 19) {
      uuid += hexDigits.substr(Math.floor(Math.random() * 4), 1);
    } else {
      uuid += hexDigits.substr(Math.floor(Math.random() * 16), 1);
    }
  }
  return uuid;
};

export const streamSmartTicketingMessage = async (
  e: MessageEvent<any>,
  accumulatedMessage: string,
  params: Params,
  eventSourceRef: MutableRefObject<EventSource | null>
) => {
  if (
    !e.data.includes('event: data') &&
    !e.data.includes('event: end') &&
    !e.data.includes('event: error') &&
    !e.data.includes('ping')
  ) {
    const jsonString = e.data.replace(/data:\s*/, '');
    
    const stringReplaced = replaceUnicodeEscapes(jsonString);

    const stringToBeParsed =
      stringReplaced.startsWith('"') && stringReplaced.endsWith('"')
        ? stringReplaced.slice(1, -1)
        : stringReplaced;

    const stringWithoutQuotations =
      stringToBeParsed.startsWith('"') && stringToBeParsed.endsWith('"')
        ? stringToBeParsed.slice(1, -1)
        : stringToBeParsed;

    const parsedData = JSON.parse(stringWithoutQuotations);

    const streamedOutput = parsedData?.ops?.find(
      (el) => el.path == '/streamed_output/-'
    );

    if (streamedOutput) {
      if (typeof streamedOutput.value == 'string') {
        accumulatedMessage += streamedOutput.value;
      }
      if (
        typeof streamedOutput.value == 'object' &&
        'content' in streamedOutput.value
      ) {
        accumulatedMessage += streamedOutput.value.content;
      }
    }

    const parsedResult = marked.parse(accumulatedMessage);

    const messageReturned = await parsedResult;

    await params.streamMessage(messageReturned, 'bot');
  }

  if (e.data.includes('event: end')) {
    eventSourceRef.current?.close();
    eventSourceRef.current = null;
  }
  if (e.data.includes('Stream Ended')) {
    eventSourceRef.current?.close();
    eventSourceRef.current = null;
  }
  return accumulatedMessage;
};

export const streamUserMessage = async (
  e: MessageEvent<any>,
  accumulatedMessage: string,
  params: Params,
  eventSourceRef: MutableRefObject<EventSource | null>,
  selectedModule: string | null,
  handleDashboardClick: (dashboardData: any | null) => void
) => {
  let messageReturned = '';
  if (e.data) {  
      const parsedData = JSON.parse(e.data);

    if (parsedData.message && !e.data.includes('Stream Ended')) {
      if (parsedData.niApplication && parsedData.niApplication.trim().toUpperCase() === 'REPORTING') {   
        
        const dashboardData = parsedData.message;         
        const buttonElement = React.createElement(
          'button',
          {
            dashboardData: dashboardData,
            className: 'chatOpenDashboard',
            onClick: () => handleDashboardClick(dashboardData),
          },
          'Open Report'
        );
        await params.streamMessage(buttonElement, 'bot');
        setTimeout(() => {
           handleDashboardClick(dashboardData);
        }, 500);       
      }
      else{
        if (parsedData.niApplication && parsedData.niApplication.trim() !== "") {
          accumulatedMessage += `**[${parsedData.niApplication}]**\n\n${parsedData.message}\n\n`;
        } else {
          accumulatedMessage += `${parsedData.message}\n\n`;
        }

        // Create a container element to render LaTeX
        const container = document.createElement('div');
        container.innerHTML = accumulatedMessage;

        // Render KaTeX in-place
        renderMathInElement(container, {
          delimiters: [
            { left: "\\(", right: "\\)", display: false },
            { left: "\\[", right: "\\]", display: true }
          ],
          throwOnError: false
        });

        messageReturned =  await marked.parse(container.innerHTML);
        await params.streamMessage(messageReturned, 'bot');
      }

    }

    if (e.data.includes('Stream Ended')) {
      eventSourceRef.current?.close();
      eventSourceRef.current = null;
    }
  } else {
    eventSourceRef.current?.close();
    eventSourceRef.current = null;
  }

  return messageReturned;
};
